import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Plus, Minus, Trash2, Loader2, ShoppingBag, Calendar, ChevronDown, ChevronUp, Clock, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderPage = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    phone: "",
    email: "",
    specialInstructions: "",
    pickupDate: null
  });
  
  // Returning customer features
  const [lookingUp, setLookingUp] = useState(false);
  const [returningCustomer, setReturningCustomer] = useState(null);
  const [showPastOrders, setShowPastOrders] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error("Failed to fetch products:", error);
      toast.error("Failed to load products. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  // Lookup returning customer
  const lookupCustomer = async (phone, email) => {
    if ((!phone || phone.length < 10) && (!email || !email.includes('@'))) return;
    
    setLookingUp(true);
    try {
      const params = new URLSearchParams();
      if (phone) params.append('phone', phone);
      if (email) params.append('email', email);
      
      const response = await axios.get(`${API}/customer/lookup?${params}`);
      
      if (response.data.found) {
        setReturningCustomer(response.data);
        // Auto-fill if fields are empty
        setCustomerInfo(prev => ({
          ...prev,
          name: prev.name || response.data.customer.name,
          phone: prev.phone || response.data.customer.phone,
          email: prev.email || response.data.customer.email
        }));
        toast.success(`Welcome back! We found your ${response.data.order_count} previous order(s).`, { duration: 4000 });
      }
    } catch (error) {
      console.error("Customer lookup failed:", error);
    } finally {
      setLookingUp(false);
    }
  };

  const addToCart = (product) => {
    const existing = cart.find(item => item.product_id === product.product_id);
    if (existing) {
      toast.info(`${product.name} is already in your order. Update quantity below.`);
      return;
    }
    setCart([...cart, { 
      product_id: product.product_id,
      product_name: product.name,
      quantity: 1,
      unit: product.unit,
      price_per_unit: product.price_per_unit,
      piece_size: "N/A",
      notes: "",
      sku_code: product.sku_code || null,
      brand: product.brand || null
    }]);
    toast.success(`Added ${product.name} to order`);
  };

  const updateQuantity = (productId, value) => {
    const qty = parseFloat(value) || 0;
    if (qty < 0) return;
    setCart(cart.map(item => 
      item.product_id === productId ? { ...item, quantity: qty } : item
    ));
  };

  const updatePieceSize = (productId, size) => {
    setCart(cart.map(item => 
      item.product_id === productId ? { ...item, piece_size: size } : item
    ));
  };

  const updateItemNotes = (productId, notes) => {
    setCart(cart.map(item => 
      item.product_id === productId ? { ...item, notes } : item
    ));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.product_id !== productId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!customerInfo.name || !customerInfo.phone || !customerInfo.email) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!customerInfo.pickupDate) {
      toast.error("Please select a pickup date");
      return;
    }

    // Validate cart items have quantity
    const invalidItems = cart.filter(item => !item.quantity || item.quantity <= 0);
    if (invalidItems.length > 0) {
      toast.error("Please enter quantity (lbs) for all items");
      return;
    }

    setSubmitting(true);
    try {
      const orderData = {
        customer_name: customerInfo.name,
        customer_phone: customerInfo.phone,
        customer_email: customerInfo.email,
        items: cart.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: item.quantity,
          unit: item.unit,
          piece_size: item.piece_size,
          notes: item.notes || ""
        })),
        special_instructions: customerInfo.specialInstructions,
        pickup_date: customerInfo.pickupDate ? format(customerInfo.pickupDate, 'yyyy-MM-dd') : null
      };

      const response = await axios.post(`${API}/orders`, orderData);
      toast.success("Order placed successfully!", { 
        description: "Confirmation email sent. Check spam/junk folder if not in inbox.",
        duration: 6000 
      });
      navigate(`/track/${response.data.order_id}`);
    } catch (error) {
      console.error("Failed to submit order:", error);
      toast.error("Failed to place order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const groupedProducts = products.reduce((acc, product) => {
    if (!acc[product.category]) acc[product.category] = [];
    acc[product.category].push(product);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#A61B1B]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F7F2]">
      {/* Header */}
      <header className="bg-white border-b-2 border-[#DCD7CC] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#1A1A1A] hover:text-[#A61B1B] transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back</span>
          </Link>
          <div className="flex items-center gap-3">
            <img 
              src="https://www.hmsusa.org/img/logo.png" 
              alt="HMS Certified" 
              className="h-8 w-auto"
            />
            <h1 className="text-lg font-[700] text-[#1A1A1A]" style={{ fontFamily: 'Fraunces, serif' }}>
              Zabiha Halal Meats
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-[#A61B1B]" />
            <span className="font-bold text-[#A61B1B]">{cart.length}</span>
          </div>
        </div>
      </header>
      
      {/* HMS Certification Banner */}
      <div className="bg-[#1A5E1F] text-white py-2 px-4 text-center">
        <p className="text-sm font-medium">HMS Certified - Hand Slaughtered Zabiha Halal</p>
      </div>

      {/* Pickup Only Notice */}
      <div className="bg-[#A61B1B] text-white py-3 px-4 text-center">
        <p className="text-sm md:text-base font-bold flex items-center justify-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          IN-STORE PICKUP ONLY • Pay When You Collect
        </p>
      </div>

      {/* Contact Banner */}
      <div className="bg-[#1A1A1A] text-white py-2 px-4 text-center">
        <p className="text-sm">
          Questions? Call or Text: <a href="tel:8476520682" className="font-bold underline">847-652-0682</a>
        </p>
      </div>

      {/* Certificate Badge */}
      <div className="bg-white border-b border-[#DCD7CC] py-3 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-center">
          <div 
            className="flex items-center gap-3 cursor-pointer hover:opacity-80"
            onClick={() => window.open('https://customer-assets.emergentagent.com/job_meatorder-pro/artifacts/p7pkvi8n_image.png', '_blank')}
            data-testid="hms-certificate"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_meatorder-pro/artifacts/p7pkvi8n_image.png" 
              alt="HMS Certificate" 
              className="h-12 w-auto rounded border"
            />
            <div className="text-left">
              <p className="text-xs font-bold text-[#1A5E1F]">Market Place on Oakton</p>
              <p className="text-xs text-[#8A8A8A]">Click to view certificate</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Step Indicator */}
        <div className="flex items-center justify-center mb-8">
          <div className={`flex items-center ${step >= 1 ? 'text-[#A61B1B]' : 'text-[#8A8A8A]'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${step >= 1 ? 'bg-[#A61B1B] text-white' : 'bg-[#DCD7CC]'}`}>1</div>
            <span className="ml-2 font-medium text-sm">Select Items</span>
          </div>
          <div className={`w-12 h-0.5 mx-3 ${step >= 2 ? 'bg-[#A61B1B]' : 'bg-[#DCD7CC]'}`} />
          <div className={`flex items-center ${step >= 2 ? 'text-[#A61B1B]' : 'text-[#8A8A8A]'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${step >= 2 ? 'bg-[#A61B1B] text-white' : 'bg-[#DCD7CC]'}`}>2</div>
            <span className="ml-2 font-medium text-sm">Your Details</span>
          </div>
        </div>

        {step === 1 && (
          <div className="space-y-4">
            {/* Returning Customer Quick Lookup */}
            <div className="bg-gradient-to-r from-[#1A5E1F] to-[#2D7A32] text-white p-4 rounded-lg" data-testid="returning-customer-lookup">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5" />
                <h3 className="font-bold">Ordered Before? Quick Reorder!</h3>
              </div>
              <div className="flex gap-2">
                <Input
                  type="tel"
                  placeholder="Enter your phone number"
                  value={customerInfo.phone}
                  onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
                  className="flex-1 bg-white text-black border-0"
                  data-testid="quick-phone-lookup"
                />
                <Button
                  type="button"
                  onClick={() => lookupCustomer(customerInfo.phone, null)}
                  disabled={lookingUp || customerInfo.phone.length < 7}
                  className="bg-white text-[#1A5E1F] hover:bg-gray-100"
                  data-testid="quick-lookup-btn"
                >
                  {lookingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : "Find My Orders"}
                </Button>
              </div>
            </div>

            {/* Quick Reorder Section - Shows when returning customer found */}
            {returningCustomer && (
              <div className="bg-[#FEF3C7] border-2 border-[#F59E0B] rounded-lg p-4" data-testid="quick-reorder-section">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">👋</span>
                    <div>
                      <p className="font-bold text-[#92400E]">Welcome back, {returningCustomer.customer.name}!</p>
                      <p className="text-sm text-[#B45309]">{returningCustomer.order_count} previous order(s)</p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setReturningCustomer(null)}
                    className="text-[#92400E] hover:bg-[#FDE68A]"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                
                {/* Favorite Items with Add to Cart */}
                {returningCustomer.favorite_items && returningCustomer.favorite_items.length > 0 && (
                  <div className="mb-3">
                    <p className="text-sm font-bold text-[#92400E] mb-2">⭐ Your Favorites - Tap to Add:</p>
                    <div className="flex flex-wrap gap-2">
                      {returningCustomer.favorite_items.map((item, idx) => {
                        const product = products.find(p => p.name === item.name);
                        const inCart = cart.find(c => c.product_name === item.name);
                        return (
                          <button
                            key={idx}
                            onClick={() => product && !inCart && addToCart(product)}
                            disabled={!product || inCart}
                            className={`px-3 py-2 rounded-full text-sm font-medium transition-all ${
                              inCart 
                                ? 'bg-[#1A5E1F] text-white cursor-default' 
                                : product 
                                  ? 'bg-white text-[#92400E] border-2 border-[#F59E0B] hover:bg-[#F59E0B] hover:text-white'
                                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                            }`}
                            data-testid={`quick-add-${idx}`}
                          >
                            {inCart ? '✓ ' : '+ '}{item.name} ({item.times_ordered}x)
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Recent Order Items */}
                {returningCustomer.recent_orders && returningCustomer.recent_orders.length > 0 && (
                  <div>
                    <p className="text-sm font-bold text-[#92400E] mb-2">📦 Last Order Items:</p>
                    <div className="bg-white rounded-lg p-3 space-y-2">
                      {returningCustomer.recent_orders[0].items.map((item, idx) => {
                        const product = products.find(p => p.name === item.name);
                        const inCart = cart.find(c => c.product_name === item.name);
                        return (
                          <div key={idx} className="flex items-center justify-between">
                            <span className="text-sm text-[#1A1A1A]">{item.name} <span className="text-[#8A8A8A]">({item.qty} lb)</span></span>
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => product && !inCart && addToCart(product)}
                              disabled={!product || inCart}
                              className={inCart ? 'bg-[#1A5E1F] text-white' : 'bg-[#A61B1B] hover:bg-[#8B1515] text-white'}
                              data-testid={`reorder-item-${idx}`}
                            >
                              {inCart ? '✓ Added' : '+ Add'}
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                    <Button
                      type="button"
                      onClick={() => {
                        // Add all items from last order
                        returningCustomer.recent_orders[0].items.forEach(item => {
                          const product = products.find(p => p.name === item.name);
                          const inCart = cart.find(c => c.product_name === item.name);
                          if (product && !inCart) {
                            addToCart(product);
                          }
                        });
                        toast.success("Added all items from your last order!");
                      }}
                      className="w-full mt-3 bg-[#F59E0B] hover:bg-[#D97706] text-white font-bold"
                      data-testid="reorder-all-btn"
                    >
                      <ShoppingBag className="w-4 h-4 mr-2" />
                      Reorder All Items From Last Order
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Category Selection Header */}
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Fraunces, serif' }}>
                Choose Your Meats
              </h2>
              <p className="text-sm text-[#8A8A8A] mt-1">Tap a category to view products</p>
            </div>

            {/* Collapsible Product Categories */}
            {Object.entries(groupedProducts).map(([category, categoryProducts]) => {
              const isExpanded = expandedCategories[category];
              const categoryItemsInCart = cart.filter(item => 
                categoryProducts.some(p => p.product_id === item.product_id)
              ).length;
              
              return (
                <div key={category} className="bg-white border-2 border-[#DCD7CC] rounded-lg overflow-hidden" data-testid={`category-${category.toLowerCase()}`}>
                  {/* Category Header - Clickable */}
                  <button
                    onClick={() => setExpandedCategories(prev => ({
                      ...prev,
                      [category]: !prev[category]
                    }))}
                    className="w-full bg-[#1A1A1A] text-white px-4 py-4 flex items-center justify-between hover:bg-[#2A2A2A] transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <h2 className="text-lg font-[700]" style={{ fontFamily: 'Fraunces, serif' }}>{category}</h2>
                      <span className="text-xs bg-white/20 px-2 py-1 rounded-full">
                        {categoryProducts.filter(p => p.available !== false).length} items
                      </span>
                      {categoryItemsInCart > 0 && (
                        <span className="text-xs bg-[#1A5E1F] px-2 py-1 rounded-full">
                          {categoryItemsInCart} selected
                        </span>
                      )}
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-6 h-6" />
                    ) : (
                      <ChevronDown className="w-6 h-6" />
                    )}
                  </button>
                  
                  {/* Products - Only shown when expanded */}
                  {isExpanded && (
                    <div className="divide-y divide-[#DCD7CC]">
                      {categoryProducts.filter(p => p.available !== false).map(product => {
                        const inCart = cart.find(item => item.product_id === product.product_id);
                        return (
                          <div 
                            key={product.product_id}
                            className="p-4 flex items-center justify-between gap-4"
                            data-testid={`product-${product.product_id}`}
                          >
                            {/* Product Image */}
                            {product.image_url && (
                              <img 
                                src={product.image_url} 
                                alt={product.name}
                                className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-baseline gap-2 flex-wrap">
                                <h3 className="font-[600] text-[#1A1A1A]">{product.name}</h3>
                                <span className="text-[#A61B1B] font-bold">${product.price_per_unit.toFixed(2)}/{product.unit}</span>
                              </div>
                              <p className="text-sm text-[#8A8A8A] truncate">{product.description}</p>
                            </div>
                            {inCart ? (
                              <span className="text-[#1A5E1F] font-medium text-sm flex-shrink-0 bg-[#E8F5E9] px-3 py-1 rounded-full">✓ Added</span>
                            ) : (
                              <Button
                                onClick={() => addToCart(product)}
                                size="sm"
                                className="bg-[#A61B1B] hover:bg-[#8B1515] text-white flex-shrink-0"
                                data-testid={`add-${product.product_id}`}
                              >
                                <Plus className="w-4 h-4 mr-1" />
                                Add
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Your Selection Section - Always Visible */}
            <div className={`bg-white border-2 ${cart.length > 0 ? 'border-[#1A5E1F]' : 'border-[#DCD7CC]'} rounded-lg overflow-hidden sticky bottom-0 shadow-lg`} data-testid="order-items">
              <div className={`${cart.length > 0 ? 'bg-[#1A5E1F]' : 'bg-[#8A8A8A]'} text-white px-4 py-3 flex items-center justify-between`}>
                <h2 className="text-lg font-[700]" style={{ fontFamily: 'Fraunces, serif' }}>
                  <ShoppingBag className="w-5 h-5 inline mr-2" />
                  Your Selection ({cart.length} {cart.length === 1 ? 'item' : 'items'})
                </h2>
                {cart.length > 0 && (
                  <span className="text-sm font-bold bg-white/20 px-3 py-1 rounded-full">
                    Ready to order
                  </span>
                )}
              </div>
              
              {cart.length === 0 ? (
                <div className="p-6 text-center text-[#8A8A8A]">
                  <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">No items selected yet</p>
                  <p className="text-sm mt-1">Tap a category above to browse and add items</p>
                </div>
              ) : (
                <div className="divide-y divide-[#DCD7CC] max-h-64 overflow-y-auto">
                  {cart.map(item => (
                    <div key={item.product_id} className="p-4 space-y-3" data-testid={`cart-item-${item.product_id}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-[600] text-[#1A1A1A]">{item.product_name}</h4>
                          <p className="text-sm text-[#A61B1B] font-medium">${item.price_per_unit.toFixed(2)}/{item.unit}</p>
                        </div>
                        <button 
                          onClick={() => removeFromCart(item.product_id)}
                          className="text-[#8A8A8A] hover:text-[#A61B1B] p-2"
                          data-testid={`remove-${item.product_id}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        {/* Quantity (lbs) */}
                        <div>
                          <Label className="text-xs font-bold text-[#1A1A1A] uppercase">Lbs Required *</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <button 
                              onClick={() => updateQuantity(item.product_id, Math.max(0.5, item.quantity - 0.5))}
                              className="w-8 h-8 border-2 border-[#DCD7CC] rounded flex items-center justify-center hover:border-[#A61B1B]"
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <Input
                              type="number"
                              step="0.5"
                              min="0.5"
                              value={item.quantity}
                              onChange={(e) => updateQuantity(item.product_id, e.target.value)}
                              className="w-20 text-center border-2 border-[#DCD7CC] focus:border-[#A61B1B]"
                              data-testid={`qty-${item.product_id}`}
                            />
                            <button 
                              onClick={() => updateQuantity(item.product_id, item.quantity + 0.5)}
                              className="w-8 h-8 border-2 border-[#DCD7CC] rounded flex items-center justify-center hover:border-[#A61B1B]"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                            <span className="text-sm font-medium">lbs</span>
                          </div>
                        </div>

                        {/* Piece Size - Different options based on product type */}
                        {(() => {
                          const name = item.product_name.toLowerCase();
                          // No cut option for most items - only ground meat and specific cuts get options
                          if (/(chop|steak|loin|shank|tongue|stew|breast|thigh|tender|shoulder|cut|whole|lollipop|drumstick|feet|gizzard|heart|leg quarter|liver|neck|wing|season|kebab)/i.test(name)) {
                            return null;
                          }
                          // Ground meat - Fine/Coarse options
                          if (/ground/i.test(name)) {
                            return (
                              <div>
                                <Label className="text-xs font-bold text-[#1A1A1A] uppercase">Grind Size</Label>
                                <Select 
                                  value={item.piece_size} 
                                  onValueChange={(value) => updatePieceSize(item.product_id, value)}
                                >
                                  <SelectTrigger className="mt-1 border-2 border-[#DCD7CC] focus:border-[#A61B1B]" data-testid={`size-${item.product_id}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="N/A">Standard</SelectItem>
                                    <SelectItem value="Fine">Fine (very small, great for kebabs & kofta)</SelectItem>
                                    <SelectItem value="Coarse">Coarse (medium small, great for burgers)</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            );
                          }
                          // Default piece size for other products
                          return (
                            <div>
                              <Label className="text-xs font-bold text-[#1A1A1A] uppercase">Piece Size</Label>
                              <Select 
                                value={item.piece_size} 
                                onValueChange={(value) => updatePieceSize(item.product_id, value)}
                              >
                                <SelectTrigger className="mt-1 border-2 border-[#DCD7CC] focus:border-[#A61B1B]" data-testid={`size-${item.product_id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="N/A">N/A</SelectItem>
                                  <SelectItem value="Small">Small</SelectItem>
                                  <SelectItem value="Medium">Medium</SelectItem>
                                  <SelectItem value="Large">Large</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          );
                        })()}
                      </div>

                      {/* Notes */}
                      <div>
                        <Label className="text-xs font-bold text-[#1A1A1A] uppercase">Special Notes / Customization</Label>
                        <Input
                          placeholder="e.g., Cut into cubes, extra trim, thin sliced..."
                          value={item.notes}
                          onChange={(e) => updateItemNotes(item.product_id, e.target.value)}
                          className="mt-1 border-2 border-[#DCD7CC] focus:border-[#A61B1B]"
                          data-testid={`notes-${item.product_id}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Continue Button - with extra margin to avoid badge overlap */}
            <div className="pb-20">
              <Button
                onClick={() => setStep(2)}
                disabled={cart.length === 0}
                className="w-full bg-[#A61B1B] hover:bg-[#8B1515] text-white py-6 text-lg font-bold uppercase relative z-[10000]"
                data-testid="continue-btn"
              >
                Continue to Your Details
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="bg-white border-2 border-[#1A1A1A] p-6" data-testid="customer-form">
            <h2 className="text-2xl font-[700] text-[#1A1A1A] mb-6" style={{ fontFamily: 'Fraunces, serif' }}>
              Your Information
            </h2>
            
            {/* Returning Customer Banner */}
            {returningCustomer && (
              <div className="bg-[#E8F5E9] border-2 border-[#4A7C59] rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-bold text-[#1A5E1F]">Welcome back, {returningCustomer.customer.name}!</p>
                    <p className="text-sm text-[#4A7C59]">You've ordered {returningCustomer.order_count} time(s) before</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowPastOrders(!showPastOrders)}
                    className="border-[#4A7C59] text-[#4A7C59]"
                  >
                    {showPastOrders ? 'Hide' : 'View'} Past Orders
                  </Button>
                </div>
                
                {/* Past Orders */}
                {showPastOrders && returningCustomer.recent_orders && (
                  <div className="mt-4 space-y-3">
                    <p className="text-sm font-bold text-[#1A5E1F]">Your Recent Orders:</p>
                    {returningCustomer.recent_orders.map((order, idx) => (
                      <div key={idx} className="bg-white p-3 rounded border">
                        <div className="flex justify-between text-sm">
                          <span className="font-medium">Order #{order.order_id}</span>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            order.status === 'completed' ? 'bg-gray-200' :
                            order.status === 'ready' ? 'bg-green-100 text-green-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>{order.status}</span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {order.items.map((item, i) => (
                            <span key={i}>{item.name} ({item.qty}){i < order.items.length - 1 ? ', ' : ''}</span>
                          ))}
                        </div>
                      </div>
                    ))}
                    
                    {/* Favorite Items */}
                    {returningCustomer.favorite_items && returningCustomer.favorite_items.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm font-bold text-[#1A5E1F]">Your Favorites:</p>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {returningCustomer.favorite_items.map((item, idx) => (
                            <span key={idx} className="bg-[#4A7C59] text-white text-xs px-2 py-1 rounded-full">
                              {item.name} ({item.times_ordered}x)
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="phone" className="text-sm font-bold text-[#1A1A1A] uppercase">Phone Number * {lookingUp && <span className="text-[#A61B1B] text-xs">(checking...)</span>}</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={customerInfo.phone}
                  onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
                  onBlur={(e) => lookupCustomer(e.target.value, customerInfo.email)}
                  placeholder="(847) 555-1234"
                  required
                  className="mt-1 border-2 border-[#DCD7CC] focus:border-[#A61B1B] p-3"
                  data-testid="customer-phone"
                />
                <p className="text-xs text-[#8A8A8A] mt-1">Enter your phone to auto-fill if you've ordered before</p>
              </div>

              <div>
                <Label htmlFor="name" className="text-sm font-bold text-[#1A1A1A] uppercase">Full Name *</Label>
                <Input
                  id="name"
                  type="text"
                  value={customerInfo.name}
                  onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})}
                  placeholder="John Smith"
                  required
                  className="mt-1 border-2 border-[#DCD7CC] focus:border-[#A61B1B] p-3"
                  data-testid="customer-name"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-sm font-bold text-[#1A1A1A] uppercase">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={customerInfo.email}
                  onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
                  onBlur={(e) => !returningCustomer && lookupCustomer(customerInfo.phone, e.target.value)}
                  placeholder="john@example.com"
                  required
                  className="mt-1 border-2 border-[#DCD7CC] focus:border-[#A61B1B] p-3"
                  data-testid="customer-email"
                />
                <p className="text-xs text-[#8A8A8A] mt-1">We'll send order updates to this email</p>
              </div>

              <div>
                <Label htmlFor="instructions" className="text-sm font-bold text-[#1A1A1A] uppercase">Additional Instructions</Label>
                <Textarea
                  id="instructions"
                  value={customerInfo.specialInstructions}
                  onChange={(e) => setCustomerInfo({...customerInfo, specialInstructions: e.target.value})}
                  placeholder="Any other instructions for your order..."
                  className="mt-1 border-2 border-[#DCD7CC] focus:border-[#A61B1B] min-h-[80px]"
                  data-testid="special-instructions"
                />
              </div>

              {/* Pickup Date */}
              <div>
                <Label className="text-sm font-bold text-[#1A1A1A] uppercase">When do you need this order? *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full mt-1 justify-start text-left font-normal border-2 border-[#DCD7CC] hover:border-[#A61B1B] p-3"
                      data-testid="pickup-date-btn"
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {customerInfo.pickupDate ? format(customerInfo.pickupDate, 'EEEE, MMMM d, yyyy') : 'Select pickup date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={customerInfo.pickupDate}
                      onSelect={(date) => setCustomerInfo({...customerInfo, pickupDate: date})}
                      disabled={(date) => date < new Date() || date < new Date().setHours(0,0,0,0)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-[#8A8A8A] mt-1">Select the date you plan to pick up your order</p>
              </div>

              {/* Order Summary */}
              <div className="bg-[#F9F7F2] p-4 border border-[#DCD7CC]">
                <h3 className="font-bold text-[#1A1A1A] mb-3 text-sm uppercase">Order Summary</h3>
                {cart.map(item => (
                  <div key={item.product_id} className="text-sm py-2 border-b border-[#DCD7CC] last:border-0">
                    <div className="flex justify-between">
                      <span className="font-medium">{item.product_name}</span>
                      <span className="text-[#A61B1B] font-bold">${item.price_per_unit.toFixed(2)}/lb</span>
                    </div>
                    <div className="text-[#8A8A8A] text-xs mt-0.5">
                      {item.quantity} lbs requested • Size: {item.piece_size}
                    </div>
                    {item.notes && <p className="text-xs text-[#8A8A8A] italic mt-0.5">"{item.notes}"</p>}
                  </div>
                ))}
                {customerInfo.pickupDate && (
                  <div className="mt-3 pt-3 border-t border-[#DCD7CC]">
                    <p className="text-sm"><strong>Pickup Date:</strong> {format(customerInfo.pickupDate, 'EEEE, MMMM d, yyyy')}</p>
                  </div>
                )}
                <div className="mt-4 p-4 bg-[#1A5E1F] text-white rounded">
                  <p className="text-sm font-bold text-center mb-1">💳 No Payment Required Now</p>
                  <p className="text-xs text-center opacity-90">Final price will be calculated at pickup based on actual weight. Pay conveniently when you collect your order.</p>
                </div>
              </div>

              <div className="flex gap-4 pt-2">
                <Button
                  type="button"
                  onClick={() => setStep(1)}
                  variant="outline"
                  className="flex-1 border-2 border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white py-5"
                  data-testid="back-btn"
                >
                  <ArrowLeft className="mr-2 w-4 h-4" />
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-[#A61B1B] hover:bg-[#8B1515] text-white py-5 text-lg font-bold uppercase"
                  data-testid="submit-order-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                      Placing...
                    </>
                  ) : (
                    "Place Order"
                  )}
                </Button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderPage;
